/**
 * วันที่ธุรกิจฝั่งหน้าจอ = เวลาไทยเสมอ ห้ามใช้ UTC
 *
 * 🚨 บั๊กจริง (worker แก้ไป 4 ก.ย. 2569 commit e3bfe4c/858a05c แต่ตกฝั่งหน้าจอไว้ 16 จุด):
 *    `new Date().toISOString().slice(0, 10)` ให้ "วัน UTC" ซึ่งช้ากว่าไทย 7 ชม.
 *    เครื่องพนักงานตั้งเวลาไทย (UTC+7) → ทุกวันช่วง 00:00-06:59 น. ค่าตั้งต้นบนจอ
 *    จะเป็น "เมื่อวาน" และวันที่ 1 ของเดือนช่วงเดียวกัน งวดจะเป็น "เดือนก่อน"
 *    ไม่มี error ไม่มีจอแดง — เห็นเป็นแค่ตัวเลขที่ผิด
 *
 * ⭐ ตัวแปลงกลางตัวเดียวของฝั่งหน้าจอ กติกาเดียวกับ `toISODate()` ของ worker
 *    (apps/workers/src/lib/utils.ts) ห้ามก๊อป `Date.now() + 7 * 3600_000` ไปเขียนเองอีก
 */
const TZ = 'Asia/Bangkok';

const isoFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** วันที่ปฏิทินไทยของ Date ที่ให้มา → 'YYYY-MM-DD' */
export function toISODate(d: Date): string {
  return isoFmt.format(d);
}

/** งวด (เดือน) ตามปฏิทินไทยของ Date ที่ให้มา → 'YYYY-MM' */
export function toPeriod(d: Date): string {
  return toISODate(d).slice(0, 7);
}

/** วันนี้ตามเวลาไทย → 'YYYY-MM-DD' */
export function todayISO(): string {
  return toISODate(new Date());
}

/** งวดปัจจุบันตามเวลาไทย → 'YYYY-MM' */
export function currentPeriod(): string {
  return toPeriod(new Date());
}

/**
 * ช่วงวันที่สำเร็จรูป — อ่านนาฬิกา "ครั้งเดียว" ต่อการเรียก 1 ครั้ง
 * 🚨 ห้ามอ่านเวลาสองครั้งในฟังก์ชันเดียว: เรียกคร่อมเที่ยงคืนพอดีจะได้ช่วงยาวเกินจริง 1 วัน
 */
export function thisMonthRange(): { from: string; to: string } {
  const today = toISODate(new Date());
  return { from: `${today.slice(0, 7)}-01`, to: today };
}

export function lastMonthRange(): { from: string; to: string } {
  const [y, m] = toISODate(new Date()).split('-').map(Number);
  const lastY = m === 1 ? y! - 1 : y!;
  const lastM = m === 1 ? 12 : m! - 1;
  const mm = String(lastM).padStart(2, '0');
  const lastDay = new Date(Date.UTC(lastY, lastM, 0)).getUTCDate();
  return { from: `${lastY}-${mm}-01`, to: `${lastY}-${mm}-${String(lastDay).padStart(2, '0')}` };
}

export function last7DaysRange(): { from: string; to: string } {
  const now = Date.now();   // ⭐ จับเวลาไว้ตัวเดียว แล้วใช้ทั้งสองขอบ
  return { from: toISODate(new Date(now - 6 * 86400_000)), to: toISODate(new Date(now)) };
}

/**
 * งวดล่าสุด n งวดตามปฏิทินไทย ใหม่→เก่า เช่น ['2026-09','2026-08',…]
 * 🚨 ห้ามใช้ Date.setMonth(-1) ไล่ถอย: วันที่ 31 จะเด้งข้ามเดือน (31 มี.ค. → 3 มี.ค.)
 *    ได้งวดซ้ำและหายไปหนึ่งงวด
 */
export function lastPeriods(n: number): string[] {
  const [y, m] = currentPeriod().split('-').map(Number);
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const shifted = m! - i;
    const year = y! + Math.floor((shifted - 1) / 12);
    const month = ((((shifted - 1) % 12) + 12) % 12) + 1;
    out.push(`${year}-${String(month).padStart(2, '0')}`);
  }
  return out;
}
